/**
 * Built-in HTML themes served as static assets from /public/themes/.
 * Using a URL (instead of storing raw customHtml) means any fix to the
 * source file is reflected immediately — no re-upload required.
 *
 * To add a new HTML theme:
 *  1. Drop the .html file in docs/html-themes/ and expose it via /public/themes/
 *  2. Add an entry below with an id, name, emoji, path, and category.
 *  3. Add the filename → path mapping to BUILTIN_THEME_FILENAME_MAP.
 *  4. Add a thumbnail config in themes/thumbnails/ThemeThumbnail.tsx.
 *  That's it — the theme selector picks it up automatically.
 */

import {type ThemeCategory} from '#/types/capsule'

export interface BuiltinHtmlTheme {
  id: string
  name: string
  emoji: string
  path: string
  category: ThemeCategory
}

export const BUILTIN_HTML_THEMES: readonly BuiltinHtmlTheme[] = [
  // ── Classic ────────────────────────────────────────────────────────────────
  // (none yet — placeholder for future clean/minimal HTML themes)

  // ── Games ──────────────────────────────────────────────────────────────────
  {
    id: 'clash-royale',
    name: 'Clash Royale',
    emoji: '⚔️',
    path: '/themes/clash-royale.html',
    category: 'games',
  },
  {
    id: 'league-of-legends',
    name: 'League of Legends',
    emoji: '🧙',
    path: '/themes/league-of-legends.html',
    category: 'games',
  },
  {
    id: 'minecraft',
    name: 'Minecraft',
    emoji: '⛏️',
    path: '/themes/minecraft.html',
    category: 'games',
  },
  {
    id: 'pixel-dungeon',
    name: 'Pixel Dungeon',
    emoji: '🎮',
    path: '/themes/pixel-dungeon.html',
    category: 'games',
  },

  // ── Cinema ─────────────────────────────────────────────────────────────────
  {
    id: 'matrix',
    name: 'Matrix',
    emoji: '🖥️',
    path: '/themes/matrix.html',
    category: 'cinema',
  },
  {
    id: 'batman',
    name: 'Batman',
    emoji: '🦇',
    path: '/themes/batman.html',
    category: 'cinema',
  },
  {
    id: 'lacasa',
    name: 'La Casa de Papel',
    emoji: '🎭',
    path: '/themes/lacasa.html',
    category: 'cinema',
  },
  {
    id: 'squidgame',
    name: 'Squid Game',
    emoji: '🦑',
    path: '/themes/squidgame.html',
    category: 'cinema',
  },

  // ── Minimal ────────────────────────────────────────────────────────────────
  {
    id: 'neubrutalism',
    name: 'Neubrutalism',
    emoji: '◼',
    path: '/themes/neubrutalism.html',
    category: 'minimal',
  },
] as const

export type BuiltinHtmlThemeId = (typeof BUILTIN_HTML_THEMES)[number]['id']

/**
 * Maps a known uploaded filename → canonical public URL.
 * Used to:
 *  1. Migrate old capsules that stored raw customHtml → htmlThemeUrl
 *  2. Auto-redirect teachers who upload a built-in file to the URL version
 */
export const BUILTIN_THEME_FILENAME_MAP: Record<string, string> = {
  'clash-royale.html': '/themes/clash-royale.html',
  'league-of-legends.html': '/themes/league-of-legends.html',
  'minecraft.html': '/themes/minecraft.html',
  'pixel-dungeon.html': '/themes/pixel-dungeon.html',
  'matrix.html': '/themes/matrix.html',
  'batman.html': '/themes/batman.html',
  'lacasa.html': '/themes/lacasa.html',
  'squidgame.html': '/themes/squidgame.html',
  'neubrutalism.html': '/themes/neubrutalism.html',
}
